import { useEffect, useMemo, useRef, useState } from "react";
import { MeetingProvider } from "@videosdk.live/react-sdk";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClockIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import imageSrc from "../logo.png";
import { useTranslation } from "react-i18next";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import MeetingContainer from "../meetingContainer/MeetingContainer";
import { AppProvider } from "../AppContextDef";
import useTimer from "../utils/useTimer";
import ConfirmBox from "../components/ConfirmBox";
import IntroPageSkeleton from "../components/skeleton/IntroPageSkeleton";
import RecordingPage from "./RecordingPage";
import useAuthFetch from "../utils/useAuthFetch";
import { fontMap } from "../utils/common";
import { enc } from "crypto-js";

function Index() {
  const currentPath = window.location.hash;
  const [currentPage, setCurrentPage] = useState("Intro");
  const [errMsg, setErrMsg] = useState(null);
  const [openErrorDialogue, setOpenErrorDialogue] = useState(false);
  const [dataFromChild, setDataFromChild] = useState("en");
  const screenShotRef = useRef(null);
  const { t, i18n } = useTranslation();
  const customFetch = useAuthFetch();
  const search = window.location.search
    ? window.location.search
    : window.location.hash.split("?")[1];
  const params = new URLSearchParams(search);
  const encryptedText = encodeURIComponent(params.get("id"));
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  const [allDetails, setAllDetails] = useState(null);
  const [agentCode, setAgentCode] = useState(null);
  const [referenceNo, setReferenceNo] = useState(null);
  const [headerSectionHeightRef, setHeaderSectionHeightRef] = useState(null);
  const [isDifferentClose, setIsDifferentClose] = useState(false);
  // When journey/start returns empty qna, we hide QnA step (set by Intropage after Start)
  const [qnaFromJourney, setQnaFromJourney] = useState(null);

  const HeaderSectionHeight = useMemo(
    () => headerSectionHeightRef?.offsetHeight,
    [headerSectionHeightRef]
  );

  const handleDataFromChild = (childData) => {
    setDataFromChild(childData);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    if (dataFromChild) {
      changeLanguage(dataFromChild);
    }
  }, [dataFromChild]);

  const isTimerActive = useMemo(() => {
    return (
      currentPage !== "Intro" &&
      currentPage !== "Thankyou" &&
      currentPage !== "Disagree" &&
      currentPage !== "Recording"
    );
  }, [currentPage]);
  const { formattedTime, resetTimer, startTimer } = useTimer();

  useEffect(() => {
    if (!isTimerActive) {
      resetTimer();
    } else {
      startTimer();
    }
  }, [isTimerActive]);

  useEffect(() => {
    if (currentPage === "  ") {
      resetTimer();
    }
  }, [currentPage]);

  const fetchAllDetails = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/v1/application/fetchDetails`;
      const method = "POST";
      const headers = {
        "Content-Type": "application/json",
      };
      if (encryptedText === "null") {
        setOpenErrorDialogue(true);
        setIsDifferentClose(false);
        setErrMsg(
          `${t(
            "We're unable to process your request due to invalid parameters. Kindly verify your input and try again."
          )}`
        );
        return;
      }
      const bodyContent = {
        encryptedText: decodeURIComponent(encryptedText),
      };

      const { response, status, message } = await customFetch(
        url,
        method,
        headers,
        bodyContent
      );

      if (
        status === 400 &&
        message === "Agent not associated with any template"
      ) {
        setOpenErrorDialogue(true);
        setIsDifferentClose(true);
        setErrMsg(
          `${t(
            "Oops! Some error occurred. Please reach out to our sales representative or write to us at vidassure@iciciprulife.com [ERR003]"
          )}`
        );
      } else if (
        status === 400 &&
        message === "System under maintenance"
      ) {
        setOpenErrorDialogue(true);
        setIsDifferentClose(true);
        setErrMsg(
          `${t(
            "Our services are temporarily unavailable. Please try again shortly."
          )}`
        );
      } else if (
        status === 400 &&
        message ===
        "Please enter valid Application Number and DOB or MobNo combination."
      ) {
        setOpenErrorDialogue(true);
        setIsDifferentClose(true);
        setErrMsg(
          `${t(
            "Incorrect Input details. Please reach out to our sales representative. [ERR002]"
          )}`
        );
      } else if (
        status === 400 &&
        message === "PIVV is already completed for this Application Number"
      ) {
        setOpenErrorDialogue(true);
        setIsDifferentClose(true);
        setErrMsg("PIVV is already completed for this Application Number.");
      } else if (
        status === 400 &&
        message === "Maximum limit reached. Please try again after 24 hours."
      ) {
        setOpenErrorDialogue(true);
        setIsDifferentClose(true);
        setErrMsg(t("Max limit reached Please try after 24 hours"));
      } else if (status === 400) {
        setOpenErrorDialogue(true);
        setIsDifferentClose(true);
        setErrMsg(
          `${t(
            "Oops! Some error occurred. Please reach out to our sales representative or write to us at vidassure@iciciprulife.com [ERR099]"
          )}`
        );
      } else {
        setAllDetails(response);
        const agentCode = response.iciciApplicationDetails.agentCode;
        setAgentCode(agentCode);
        setReferenceNo(response.applicationNo);
      }

      if (response?.currentJourneyIndex === 14) {
        setOpenErrorDialogue(true);
        setErrMsg(
          t("You have only one chance left to complete the verification.")
        );
      }

      if (response?.currentJourneyIndex > 15) {
        setOpenErrorDialogue(true);
        setIsDifferentClose(true);
        setErrMsg(t("Max limit reached Please try after 24 hours"));
      }

      if (currentPage === "Intro" && response?.currentJourneyIndex > 0) {
        resetTimer();
      }
    } catch (e) {
      setOpenErrorDialogue(true);
      setIsDifferentClose(true);
      setErrMsg(
        `${t(
          "Oops! Some error occurred. Please reach out to our sales representative or write to us at vidassure@iciciprulife.com [ERR003]"
        )}`
      );
    }
  };
  useEffect(() => {
    if (!currentPath.startsWith("#/recording")) {
      fetchAllDetails();
    }
  }, []);

  useEffect(() => {
    if (currentPath.startsWith("#/recording")) {
      setCurrentPage("Recording");
    }
  }, [currentPath]);

  const steps = [
    {
      title: "Start Your Journey",
      value: "Intro",
    },
    {
      title: "Personal Details",
      value: "PersonalDetails",
    },
    {
      title: "Policy Details",
      value: "PolicyDetails",
    },
    {
      title: "Video Consent",
      value: "VideoConsent",
    },
    // Hide QnA step when backend returns empty qna (same as no QnA section)
    ...(allDetails?.qnaTemplateId && (qnaFromJourney == null || (Array.isArray(qnaFromJourney) && qnaFromJourney.length > 0)) ? [{
      title: "QnA",
      value: "QnA",
    }] : []),
  ];

  const currentStepIndex = steps.findIndex(
    (step) => step.value === currentPage
  );

  let productLength =
    allDetails?.iciciApplicationDetails?.productDetails.length;

  let isMobileOrTab = isMobile || isTab;

  const fontFamilyCSS = fontMap[dataFromChild] || "Lato";


  return (
    <div
      ref={screenShotRef}
      className="flex flex-col  overflow-y-hidden h-[100%] lg:flex-row "
    >
      <ToastContainer style={{ fontFamily: fontFamilyCSS }} />

      {currentPage !== "Recording" && (
        <div
          ref={setHeaderSectionHeightRef}
          style={{ width: !isMobileOrTab && 325 }}
          className={`bg-white lg:bg-[#F3F7FD] lg:px-10   pt-2 lg:pb-0 shadow-lg flex flex-col ${currentPage !== "PolicyDetails" &&
            productLength > 1 &&
            "justify-between"
            }  sticky top-0 z-40 `}
        >
          {productLength > 1 &&
            currentPage === "PolicyDetails" &&
            !isMobileOrTab ? (
            <div
              className={`${productLength > 1 &&
                currentPage === "PolicyDetails" &&
                "lg:sticky top-0"
                }`}
            >
              <div className="px-3 lg:px-0 mb-10 sticky top-0">
                <img src={imageSrc} alt="logo" width={isMobileOrTab && 150} />
              </div>
              <div className="mt-3">
                <p className="text-sm lg:text-base font-bold text-black-150">
                  {`${t(`Application number`)}: ${referenceNo}`}
                </p>
              </div>
              <div className="flex flex-1 lg:flex-col pt-5 px-3 gap-2 pb-4 lg:pb-0 lg:px-0 lg:pt-44 overflow-x-auto lg:overflow-x-visible lg:justify-center">
                {steps.map(({ title, value }, index) => {
                  const isCompleted = index < currentStepIndex; // Step is completed
                  const isCurrent = value === currentPage; // Current step
                  return (
                    <div
                      key={index}
                      className={`${index !== 0 && "lg:mt-4"} ${isCurrent
                        ? "bg-blue-150 border-blue-150 md:bg-transparent"
                        : isMobileOrTab &&
                        "border border-gray-275 md:border-hidden"
                        } rounded-md lg:rounded-none flex items-center px-2 py-[6px] lg:p-0 `}
                    >
                      {isCompleted ? (
                        <div className="hidden lg:block">
                          <CheckCircleIcon className="w-5 h-5 text-green-150" />{" "}
                        </div>
                      ) : (
                        <div
                          className={`hidden lg:block w-4 h-4 rounded-full ${isCurrent
                            ? "border-black-150 border "
                            : "border-gray-150 border "
                            }
                }`}
                        ></div>
                      )}
                      <p
                        className={`${isCompleted ? "lg:ml-3" : "lg:ml-4"
                          }  text-sm lg:text-base font-bold whitespace-nowrap truncate ${isCurrent
                            ? isMobileOrTab
                              ? "text-white"
                              : "text-black"
                            : isCompleted && isMobileOrTab
                              ? "text-black-150"
                              : "text-gray-150"
                          } mb-0`}
                      >
                        {title}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div className="px-3  lg:px-0">
                <img src={imageSrc} alt="logo" width={isMobileOrTab && 150} />
              </div>
              <div className="hidden md:block mt-3">
                <p className="text-sm lg:text-base font-bold text-black-150">
                  {`${t(`Application number`)}: ${referenceNo}`}
                </p>
              </div>
              <div className="flex flex-1 lg:flex-col pt-3  px-3 gap-2 pb-4 lg:pb-0 lg:px-0 lg:pt-0 overflow-x-auto lg:overflow-x-visible lg:justify-center">
                {steps.map(({ title, value }, index) => {
                  const isCompleted = index < currentStepIndex; // Step is completed
                  const isCurrent = value === currentPage; // Current step
                  return (
                    <div
                      key={index}
                      className={`${index !== 0 && "lg:mt-4"} ${isCurrent
                        ? "bg-blue-150 border-blue-150 lg:bg-transparent"
                        : isMobileOrTab &&
                        "border border-gray-275 lg:border-hidden"
                        } rounded-md lg:rounded-none flex items-center px-2 py-[6px] lg:p-0 `}
                    >
                      {isCompleted ? (
                        <div className="hidden lg:block">
                          <CheckCircleIcon className="w-5 h-5 text-green-150" />{" "}
                        </div>
                      ) : (
                        <div
                          className={`hidden lg:block w-4 h-4 rounded-full ${isCurrent
                            ? "border-black-150 border "
                            : "border-gray-150 border "
                            }
                }`}
                        ></div>
                      )}
                      <p
                        className={`${isCompleted ? "lg:ml-3" : "lg:ml-4"
                          }  text-sm lg:text-base font-bold whitespace-nowrap truncate ${isCurrent
                            ? isMobileOrTab
                              ? "text-white"
                              : "text-black"
                            : isCompleted && isMobileOrTab
                              ? "text-black-150"
                              : "text-gray-150"
                          } mb-0`}
                      >
                        {t(`${title}`)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {currentPage !== "Intro" &&
            currentPage !== "Thankyou" &&
            isMobileOrTab && (
              <div className="absolute top-2 right-2">
                <div className="flex lg:hidden items-center bg-orange-250 text-white rounded-md p-2">
                  <ClockIcon className="w-4 h-4  font-bold" />
                  <p className="text-sm  mb-0 ml-2 font-medium">
                    {formattedTime}
                  </p>
                </div>
              </div>
            )}
        </div>
      )}
      {allDetails || currentPage === "Recording" ? (
        <div className="w-full h-full  flex lg:flex-col ">
          <AppProvider
            referenceNo={referenceNo}
            timer={formattedTime}
            allDetails={allDetails}
            agentCode={agentCode}
            currentPage={currentPage}
            encryptedText={encryptedText}
            HeaderSectionHeight={HeaderSectionHeight}
            onQnaFromJourney={setQnaFromJourney}
          >
            {currentPage === "Recording" && (
              <RecordingPage formattedTime={formattedTime} />
            )}
            {currentPage !== "Recording" && (
              <MeetingProvider
                config={{
                  meetingId: allDetails?.videoSDKRoomId,
                  micEnabled: false,
                  webcamEnabled: true,
                  name: "Raman",
                  signalingBaseUrl: "api.videosdk.live",
                  multiStream: false
                }}
                token={allDetails?.videoSDKToken}
                joinWithoutUserInteraction={false}
              >
                <MeetingContainer
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  referenceNo={referenceNo}
                  screenShotRef={screenShotRef}
                  onDataSend={handleDataFromChild}
                ></MeetingContainer>
              </MeetingProvider>
            )}
          </AppProvider>
        </div>
      ) : (
        <IntroPageSkeleton />
      )}
      {openErrorDialogue && (
        <ConfirmBox
          open={openErrorDialogue}
          setOpen={setOpenErrorDialogue}
          buttonText={t("Close")}
          buttonHandler={() => {
            if (isDifferentClose) {
              setOpenErrorDialogue(false);
              window.location.replace("https://www.iciciprulife.com/");
            } else {
              setOpenErrorDialogue(false);
            }
          }}
          text={errMsg}
        />
      )}
    </div>
  );
}

export default Index;
